import { useMemo } from "react";
import { Plane, Plus, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { formatDay } from "./format";
import TripCard from "./TripCard";

/**
 * Past trips in a country.
 *
 * Clients first, each one row standing for every trip made to it, because that
 * is what makes a list of trips readable once there are more than a few. Trips
 * with no client follow, in full: they belong to nothing and there is nothing
 * to fold them into.
 */
export default function TripsPanel({
  countryName, clients, byClient, looseTrips, loading, language, t,
  onOpenClient, onAddTrip, onEditTrip, onDeleteTrip,
}) {
  // Clients with trips first, most recently visited at the top; the ones with
  // nothing recorded yet after them, since a row saying nothing has happened
  // is worth less than one that says when it did.
  const rows = useMemo(() => {
    const withTrips = clients.map((c) => {
      const list = byClient.get(c.id) || [];
      const last = list.reduce(
        (acc, tr) => {
          const when = tr.visited_from || "";
          return when > acc ? when : acc;
        },
        ""
      );
      return { client: c, count: list.length, last };
    });
    return withTrips.sort((a, b) => {
      if (Boolean(a.count) !== Boolean(b.count)) return b.count - a.count;
      return (b.last || "").localeCompare(a.last || "");
    });
  }, [clients, byClient]);

  return (
    <div className="p-6" data-testid="trasferte-trips-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-semibold text-slate-800 min-w-0 break-words">
          {t("trasferte.tripsIn")} {countryName}
        </h2>
        <Button
          data-testid="trasferte-add-trip-btn"
          onClick={onAddTrip}
          className="bg-indigo-700 hover:bg-indigo-600 text-white shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("trasferte.addTrip")}
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">{t("trasferte.loading")}</p>
      ) : rows.length === 0 && looseTrips.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Plane className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">{t("trasferte.noVisits")}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {rows.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wide text-slate-400">
                {t("trasferte.clients")}
              </h3>
              {rows.map(({ client, count, last }) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => onOpenClient(client.id)}
                  data-testid={`client-row-${client.id}`}
                  className="w-full text-left border border-slate-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 break-words">{client.name}</p>
                    {client.city && (
                      <p className="text-sm text-slate-500 break-words">{client.city}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm text-slate-700">
                      {count === 1
                        ? t("trasferte.visitsOne")
                        : count
                        ? t("trasferte.visitsCount", { count })
                        : t("trasferte.visitsNone")}
                    </p>
                    {last && <p className="text-xs text-slate-400">{formatDay(last, language)}</p>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {looseTrips.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400">
                {t("trasferte.withoutClient")}
              </h3>
              {looseTrips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  t={t}
                  language={language}
                  onEdit={() => onEditTrip(trip)}
                  onDelete={() => onDeleteTrip(trip)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
